import ImageLayer from 'ol/layer/Image'
import ImageStatic from 'ol/source/ImageStatic'
import { transformExtent } from 'ol/proj'
import ILayers from 'utils/mapUtils/ILayers'
import {
    MODEL_AROME,
    MODEL_ARPEGE,
    EPSG_4326,
    EPSG_3857,
} from '../../../station/components/mapSituation/RadarService'

const HEADER_BAR_CUTOFF = 0.085
const TOP_LEFT_BOX_Y_CUTOFF = 0.18
const TOP_LEFT_BOX_X_CUTOFF = 0.55
const TOP_RIGHT_BADGE_Y_CUTOFF = 0.12
const TOP_RIGHT_BADGE_X_CUTOFF = 0.65
const BOTTOM_LEGEND_Y_MIN = 0.91
const BOTTOM_LEGEND_Y_MAX = 0.99
const BOTTOM_LEGEND_X_MIN = 0.06
const BOTTOM_LEGEND_X_MAX = 0.94
const BOTTOM_EDGE_CUTOFF = 0.985

const DARK_COLOR_THRESHOLD = 25
const GRAYSCALE_DIFF_THRESHOLD = 12
const DARK_OCEAN_RED_MAX = 50
const DARK_OCEAN_GREEN_MAX = 75
const DARK_OCEAN_BLUE_MAX = 120
const DARK_OCEAN_DIFF_MAX = 55

const processedUrlCache = {}

export const processRadarFluxImage = (imageUrl) => {
    if (!imageUrl) return Promise.resolve('')
    if (processedUrlCache[imageUrl]) {
        return Promise.resolve(processedUrlCache[imageUrl])
    }

    return new Promise((resolve) => {
        const img = document.createElement('img')
        img.crossOrigin = 'Anonymous'
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas')
                canvas.width = img.naturalWidth || img.width
                canvas.height = img.naturalHeight || img.height
                const context = canvas.getContext('2d')
                if (!context) {
                    resolve(imageUrl)
                    return
                }

                context.drawImage(img, 0, 0)
                const { width, height } = canvas
                const imgData = context.getImageData(0, 0, width, height)
                const pixelData = imgData.data
                const uint32Data = new Uint32Array(imgData.data.buffer)

                uint32Data.forEach((_pixel, pixelIndex) => {
                    const x = pixelIndex % width
                    const y = Math.floor(pixelIndex / width)
                    const relX = x / width
                    const relY = y / height

                    const isTopHeaderBar = relY < HEADER_BAR_CUTOFF
                    const isTopLeftBox = relY < TOP_LEFT_BOX_Y_CUTOFF && relX < TOP_LEFT_BOX_X_CUTOFF
                    const isTopRightBadge = relY < TOP_RIGHT_BADGE_Y_CUTOFF && relX > TOP_RIGHT_BADGE_X_CUTOFF
                    const isBottomLegendBar = relY > BOTTOM_LEGEND_Y_MIN && relY < BOTTOM_LEGEND_Y_MAX && relX > BOTTOM_LEGEND_X_MIN && relX < BOTTOM_LEGEND_X_MAX
                    const isBottomEdge = relY > BOTTOM_EDGE_CUTOFF

                    const index = pixelIndex * 4
                    if (isTopHeaderBar || isTopLeftBox || isTopRightBadge || isBottomLegendBar || isBottomEdge) {
                        pixelData[index + 3] = 0
                        return
                    }

                    const red = pixelData[index]
                    const green = pixelData[index + 1]
                    const blue = pixelData[index + 2]

                    const maxVal = Math.max(red, green, blue)
                    const minVal = Math.min(red, green, blue)
                    const diff = maxVal - minVal

                    const isDark = maxVal < DARK_COLOR_THRESHOLD
                    const isGrayscaleBorder = diff <= GRAYSCALE_DIFF_THRESHOLD
                    const isDarkOcean = (red < DARK_OCEAN_RED_MAX && green < DARK_OCEAN_GREEN_MAX && blue < DARK_OCEAN_BLUE_MAX && diff < DARK_OCEAN_DIFF_MAX)

                    if (isDark || isGrayscaleBorder || isDarkOcean) {
                        pixelData[index + 3] = 0
                    }
                })

                context.putImageData(imgData, 0, 0)
                const transparentDataUrl = canvas.toDataURL('image/png')
                processedUrlCache[imageUrl] = transparentDataUrl
                resolve(transparentDataUrl)
            } catch {
                resolve(imageUrl)
            }
        }
        img.onerror = () => resolve(imageUrl)
        img.src = imageUrl
    })
}

class _RadarLayer extends ILayers {
    constructor({ url, model = MODEL_AROME, opacity = 0.85, visible = true } = {}) {
        super()
        this.rawUrl = url
        this.model = model
        this.opacity = opacity
        this.visible = visible
        this.extent = this.computeExtent(model)
        this.layer = this.createLayer()
        if (url) {
            this.updateUrl(url)
        }
    }

    computeExtent(model) {
        if (model === MODEL_ARPEGE) {
            return transformExtent([-9.0, 40.0, 11.9, 53.9], EPSG_4326, EPSG_3857)
        }
        return transformExtent([-8.99, 40.0, 12.0, 53.99], EPSG_4326, EPSG_3857)
    }

    createLayer() {
        return new ImageLayer({
            visible: this.visible,
            opacity: this.opacity,
            zIndex: 15,
        })
    }

    updateUrl(url) {
        this.rawUrl = url
        if (!url) {
            if (this.layer) {
                this.layer.setSource(undefined)
            }
            return
        }

        processRadarFluxImage(url).then((transparentUrl) => {
            if (this.rawUrl === url && this.layer) {
                this.layer.setSource(new ImageStatic({
                    url: transparentUrl,
                    imageExtent: this.extent,
                    projection: EPSG_3857,
                }))
            }
        })
    }

    setModel(model) {
        this.model = model
        this.extent = this.computeExtent(model)
        if (this.rawUrl) {
            this.updateUrl(this.rawUrl)
        }
    }

    setOpacity(opacity) {
        this.opacity = opacity
        if (this.layer) {
            this.layer.setOpacity(opacity)
        }
    }

    getLayer() {
        return this.layer
    }
}

export default function RadarLayer(args) {
    return new _RadarLayer(args)
}
